import { useState, useEffect } from "react";
import Loader from "../shared/loader";
import Api from '../api/api';
import { Link } from 'react-router-dom';
import Pagination from "../shared/pagination";
import { saveAs } from 'file-saver';
import arrayUtilities from "../../utilities/arrayUtilities";
import {Modal, Button} from 'react-bootstrap'
import formUtilities from "../../utilities/formUtilities";
import stringHelpers from "../../utilities/stringHelpers";
import {ErrorModal} from "../../utilities/errorModal";
import { ColumnSortTrigger } from "../shared/columnSortTrigger";
import { SearchBarWithDebounce } from "../shared/searchBarWithDebounce";
import { FiltersModalTrigger } from "../shared/filtersModalTrigger";
import { SitesFiltersModal } from "./sitesFiltersModal";
import { SitesScheduleModal } from "../reports/schedule-modals/sitesScheduleModal";
import { AppliedFilterPill, renderTagFilterItemLabel } from "../shared/appliedFilterPill";
import appliedFiltersUtilities from "../../utilities/appliedFiltersUtilities";

export default function Sites(props) {

    const sitesSortPreferenceKey = `${props.user.id}-sitesSortPreference`;
    const getSitesSortPreference = () => {
        const preference = stringHelpers.sortPreference(sitesSortPreferenceKey);
        return {
            sortBy: preference.sortBy || "name",
            sortDirection: preference.sortDirection || 0
        };
    };

    const [sites, setSites] = useState(null);
    const [clients, setClients] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null)
    const [selectedTags, setSelectedTags] = useState([])
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [pageNumber, setPageNumber] = useState(0);
    const [copyContent, setCopyContent] = useState(null)
    const [copyName, setCopyName] = useState(null)
    const [copyClient, setCopyClient] = useState(null)
    const [errorMessage, setErrorMessage] = useState(null)
    const [currentSortBy, setCurrentSortBy] = useState(getSitesSortPreference().sortBy);
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [showFilterPills, setShowFilterPills] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const [showScheduleExportModal, setShowScheduleExportModal] = useState(false);

    const scheduleInitialParameters = { clientId: selectedClient, tagIds: selectedTags }

    const closeScheduleExportModal = () => setShowScheduleExportModal(false)

    var _sitesAbortController = null;

    const getSites = function (query, pageNumber, clientId, sortBy, sortDirection, tags) {
        setIsLoading(true);
        if (_sitesAbortController) {
            _sitesAbortController.abort();
        }
        _sitesAbortController = new AbortController();
        const signal = _sitesAbortController.signal;
        Api.searchSites(
            (data) => {
                setSites(data);
                setIsLoading(false);
            },
            (error) => {
                setSites(null);
                setIsLoading(false);
            },
            query,
            pageNumber,
            !stringHelpers.isNullOrEmpty(clientId) ? clientId : null,
            sortBy,
            sortDirection,
            tags,
            props.organisationId,
            props.token,
            signal
        );
    };

    const getClients = () => {
        Api.getClients(
            data => setClients(data),
            error => console.log(error),
            props.organisationId,
            props.token
        )
    }

    useEffect(() => {
        const sortPreference = getSitesSortPreference();
        getSites(search, pageNumber, selectedClient, sortPreference.sortBy, sortPreference.sortDirection, selectedTags);
        getClients()
    }, []);

    const changePageNumber = (pageNum) => {
        const sortPreference = getSitesSortPreference();
        getSites(search, pageNum, selectedClient, sortPreference.sortBy, sortPreference.sortDirection, selectedTags);
        setPageNumber(pageNum);
    }

    const handleRefresh = () => {
        const sortPreference = getSitesSortPreference();
        getSites(search, pageNumber, selectedClient, sortPreference.sortBy, sortPreference.sortDirection, selectedTags);
        window.scrollTo({ top: 0 });
    }

    const handleSortTriggerClick = (sortBy, sortDirection) => {
        getSites(search, pageNumber, selectedClient, sortBy, sortDirection, selectedTags);
        if (currentSortBy !== sortBy) {
            setCurrentSortBy(sortBy);
        }
    }

    const clearFilters = () => {
        setSelectedClient(null)
        setSelectedTags([])
        setAppliedFilters(null)
        setShowFilterPills(false)
        setPageNumber(0)
        const sortPreference = getSitesSortPreference();
        getSites(search, 0, null, sortPreference.sortBy, sortPreference.sortDirection, [])
    }

    const clearFilterByLabel = (label) => {
        const sortPreference = getSitesSortPreference();
        switch (label) {
            case "Client":
                setSelectedClient(null)
                getSites(search, 0, null, sortPreference.sortBy, sortPreference.sortDirection, selectedTags)
                break
            case "Tags":
                setSelectedTags([])
                getSites(search, 0, selectedClient, sortPreference.sortBy, sortPreference.sortDirection, [])
            default:
                break
        }
        setPageNumber(0)
    }

    const handleFilterPillClose = closedLabel => {
        clearFilterByLabel(closedLabel)
        setAppliedFilters(prevState => {
            const updatedFilters = { ...prevState }
            updatedFilters[closedLabel] = null
            return appliedFiltersUtilities.hasRemainingFilters(updatedFilters) ? updatedFilters : null
        })
    }

    const handleFilterPillItemRemove = (label, remainingIds) => {
        setAppliedFilters(prevState => appliedFiltersUtilities.keepMultiSelectionItems(prevState, label, remainingIds))
        removeMultiSelectFilterItemByLabel(label, remainingIds)
    }

    const removeMultiSelectFilterItemByLabel = (label, remainingIds) => {
        const sortPreference = getSitesSortPreference();
        switch (label) {
            case "Tags":
                setSelectedTags(remainingIds)
                getSites(search, 0, selectedClient, sortPreference.sortBy, sortPreference.sortDirection, remainingIds)
                break
            default:
                break
        }
        setPageNumber(0)
    }

    const handleExport = () => {
        Api.exportSitesCsv(
            !stringHelpers.isNullOrEmpty(selectedClient) ? selectedClient : null,
            selectedTags,
            props.organisationId,
            (blob, filename) => { saveAs(blob, filename) },
            error => console.log(error),
            props.token
        )
    }

    const handleCopy = () => {
        Api.copySite(
            data => {
                if (data.isError) {
                    setErrorMessage(data.errorMessage)
                } else {
                    const sortPreference = getSitesSortPreference();
                    getSites(search, pageNumber, selectedClient, sortPreference.sortBy, sortPreference.sortDirection, selectedTags)
                    setCopyClient(null)
                    setCopyName(null)
                }
                setCopyContent(null)
            },
            error => setErrorMessage(error.errorMessage),
            copyContent.id,
            copyName,
            copyClient,
            props.organisationId,
            props.token
        )
    }

    return <>
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="pr-0">
                                    <h5 className="fs-0 mb-0 text-nowrap py-2 py-xl-0">Sites</h5>
                                </div>

                                <div className="d-flex align-items-center">
                                    <div className='mr-2'>
                                        <SearchBarWithDebounce
                                            onSearchQueryChange={ query => {
                                                setSearch(query)
                                                setPageNumber(0)
                                                const sortPreference = getSitesSortPreference();
                                                getSites(query, 0, selectedClient, sortPreference.sortBy, sortPreference.sortDirection, selectedTags)
                                            } }
                                        />
                                    </div>
                                    <div className='mr-2'>
                                        <FiltersModalTrigger onClick={ () => setShowFiltersModal(true) } />
                                    </div>
                                    <button className="btn btn-primary btn-sm mr-1 ml-1 nowrap" onClick={handleRefresh}>
                                        <i className="fas fa-redo mr-1"></i> Refresh
                                    </button>
                                    <button
                                        className="btn btn-dark btn-sm mr-1 ml-1 nowrap"
                                        onClick={handleExport}>
                                        <i className="fas fa-download"></i> Export
                                    </button>
                                    <button
                                        onClick={ () => setShowScheduleExportModal(true) }
                                        className="btn btn-sm btn-secondary nowrap ml-1"
                                    >
                                        Schedule Report<i className="ml-2 far fa-clock"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        {
                            showFilterPills && appliedFilters &&
                            <div className="px-3 py-2 border-top d-flex align-items-center flex-wrap">
                                {
                                    Object.entries(appliedFilters).map(
                                        ([label, value]) => <AppliedFilterPill
                                            key={ label }
                                            label={ label }
                                            value={ value }
                                            renderItemLabel={ renderTagFilterItemLabel }
                                            onTextClick={ () => setShowFiltersModal(true) }
                                            onCloseClick={ handleFilterPillClose }
                                            onRemoveItem={ handleFilterPillItemRemove }
                                        />
                                    )
                                }
                                { appliedFilters && Object.keys(appliedFilters).length > 0 && <button type="button" className="btn btn-none p-0 m-0 fs--1 nowrap" onClick={ () => clearFilters() }>Clear all filters</button> }
                            </div>
                        }
                        <table className="table table-sm mb-0 table-striped table-dashboard fs--1 data-table border-bottom border-200 no-footer ">
                            <thead className="bg-200 text-900">
                                <tr>
                                    <th>
                                        <ColumnSortTrigger
                                            onClick={ direction => handleSortTriggerClick("clientname", direction) }
                                            currentSortBy={currentSortBy}
                                            sortBy="clientname"
                                            localStorageKey={sitesSortPreferenceKey}
                                            label="Client"
                                        />
                                    </th>
                                    <th>
                                        <ColumnSortTrigger
                                            onClick={ direction => handleSortTriggerClick("name", direction) }
                                            currentSortBy={currentSortBy}
                                            sortBy="name"
                                            localStorageKey={sitesSortPreferenceKey}
                                            label="Site"
                                        />
                                    </th>
                                    <th>
                                        <ColumnSortTrigger
                                            onClick={ direction => handleSortTriggerClick("sitecode", direction) }
                                            currentSortBy={currentSortBy}
                                            sortBy="sitecode"
                                            localStorageKey={sitesSortPreferenceKey}
                                            label="Site Code"
                                        />
                                    </th>
                                    <th className="text-center">Status</th>
                                    <th>&nbsp;</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    isLoading
                                    ? <tr><td colSpan={5}><Loader /></td></tr>
                                    : !arrayUtilities.isNullOrEmpty(sites?.items) && sites.items.map((site) => {
                                        var url = `/organisation/${props.organisationId}/client/${site.clientId}/site/${site.id}`;
                                        return (
                                            <tr key={site.id}>
                                                <td>{site.clientName}</td>
                                                <td><Link to={url}><h6 className="mb-0">{site.name}</h6></Link></td>
                                                <td>{site.siteCode}</td>
                                                <td className="text-center">{site.isActive && <><i className="fas fa-check text-success"></i></>}{!site.isActive && <><i className="fas fa-times text-danger"></i></>}</td>
                                                <td className="text-right">
                                                    <button onClick={() => {
                                                        setCopyContent(site)
                                                        setCopyName(`${site.name} (copy)`)
                                                        setCopyClient(site.clientId)
                                                    }} className="btn btn-none p-0 m-0 pr-3"><i className="far fa-copy"></i></button>
                                                </td>
                                            </tr>)
                                    })
                                }
                            </tbody>
                        </table>
                        {
                            !isLoading &&
                            <div className="d-flex justify-content-center row align-items-center pt-2">
                                <div className="col-auto align-items-center">
                                    <Pagination
                                        totalResults={sites?.totalCount}
                                        currentPage={pageNumber}
                                        changePageNumber={changePageNumber} />
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </div>
        {copyContent && <Modal show onHide={ () => {
            setCopyContent(null)
            setCopyName(null)
            setCopyClient(null)
        } } centered>
            <Modal.Header>
                <Modal.Title className="d-flex gap-2 align-items-center">Copy site</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row mb-2">
                    <div className="col-md-3 col-12 ">
                        <p className="font-weight-semi-bold  mt-1 mb-1">Name<span className="req"></span></p>
                    </div>
                    <div className="col-md-9 col-12">
                        <div className="form-group mb-1">
                            <input required className="form-control mb-0" type="text" autoFocus={ true } value={ copyName } onChange={ (e) => {
                                setCopyName(e.target.value);
                                formUtilities.requiredField(e.target.parentNode, e.target.value)
                            } } />
                            <small className='invalid-feedback text-danger'>Please enter a name</small>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-3 col-12 ">
                        <p className="font-weight-semi-bold  mt-1 mb-1">Client<span className="req"></span></p>
                    </div>
                    <div className="col-md-9 col-12">
                        <div className="form-group mb-1">
                            <select 
								onChange={e => setCopyClient(e.target.value)}
								value={copyClient}
								className="border rounded w-100 py-1 px-3" 
							>
                                <option value="">Select a client</option>
								{
                                    !arrayUtilities.isNullOrEmpty(clients) &&
                                    clients.map(
                                        client => <option value={client.id} key={client.id}>{client.name}</option>
                                    )
                                }
							</select>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button size="sm" variant='secondary' onClick={ () => {
                    setCopyContent(null)
                    setCopyName(null)
                    setCopyClient(null)
                } }>Cancel</Button>
                <Button disabled={stringHelpers.isNullOrEmpty(copyName) || stringHelpers.isNullOrEmpty(copyClient) } size="sm" variant='primary' onClick={ () => handleCopy() }>Copy</Button>
            </Modal.Footer>
        </Modal>}

        {
            !stringHelpers.isNullOrEmpty(errorMessage) &&
            <ErrorModal message={errorMessage} onHide={() => setErrorMessage(null)} />
        }
        {
            showFiltersModal &&
            <SitesFiltersModal
                showModal={ showFiltersModal }
                handleClose={ () => setShowFiltersModal(false) }
                handleConfirm={ (draftFilters) => {
                    setSelectedClient(draftFilters.selectedClient)
                    setSelectedTags(draftFilters.selectedTags)
                    setAppliedFilters(draftFilters.appliedFilters)
                    setShowFilterPills(true)
                    setShowFiltersModal(false)
                    setPageNumber(0)
                    const sortPreference = getSitesSortPreference();
                    getSites(search, 0, draftFilters.selectedClient, sortPreference.sortBy, sortPreference.sortDirection, draftFilters.selectedTags)
                } }
                initialSelectedClient={ selectedClient }
                initialAppliedFilters={ appliedFilters }
                initialSelectedTags={ selectedTags }
                clients={ clients || [] }
                tags={ props.tags || [] }
                key="sites-filters-modal"
            />
        }

        {
            showScheduleExportModal &&
            <SitesScheduleModal
                clients={ clients }
                tags={ props.tags || [] }
                organisationId={ props.organisationId }
                token={ props.token }
                initialParameters={ scheduleInitialParameters }
                onCancel={ closeScheduleExportModal }
                onSaved={ closeScheduleExportModal }
            />
        }
    </>;
}